import { lazy, Suspense, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation } from "react-router-dom";
import SEO from "./SEO";
import useSoundMixer from "../hooks/useSoundMixer";
import useSleepTimer from "../hooks/useSleepTimer";
import PlaybackControls from "./PlaybackControls";
import Sound from "./Sound";
import SoundList from "./SoundList";
import { normalizePathname } from "../utils/pathname";
const CalmGallery = lazy(() => import("./CalmGallery"));
const LiveGallery = lazy(() => import("./LiveGallery"));

const NatureTouch = () => {
  const mixer = useSoundMixer();
  const { masterVolume, setMasterVolume, sounds, volumes, playingSounds, isPaused, setVolume, togglePlay, playMix, pause, resume, clear } = mixer;
  const pathname = normalizePathname(useLocation().pathname);
  const isGallery = pathname === "/gallery";
  const isLiveGallery = pathname === "/live-gallery";
  const [calmScene, setCalmScene] = useState(null);
  useEffect(() => { if (!isGallery) setCalmScene(null); }, [isGallery]);
  const timer = useSleepTimer(pause);
  const [headerSlot, setHeaderSlot] = useState(null);
  useEffect(() => { setHeaderSlot(document.getElementById("mixer-header-controls")); }, []);
  const cancelTimer = timer.cancel;
  useEffect(() => {
    if (playingSounds.length === 0) cancelTimer();
  }, [playingSounds.length, cancelTimer]);
  const previousLiveGallery = useRef(isLiveGallery);
  useEffect(() => {
    if (!isLiveGallery) return;
    const stop = () => { cancelTimer(); clear(); };
    const beforeNavigation = event => {
      if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const tab = event.target.closest('.playlist-tabs button');
      if (tab && tab.getAttribute('aria-current') !== 'page') { stop(); return; }
      const link = event.target.closest('a[href]');
      if (!link || (link.target && link.target !== '_self')) return;
      const url = new URL(link.href, window.location.href);
      if (url.origin === window.location.origin && normalizePathname(url.pathname) !== '/live-gallery') stop();
    };
    const onHistoryChange = () => {
      if (normalizePathname(window.location.pathname) !== '/live-gallery') stop();
    };
    document.addEventListener('click', beforeNavigation, true);
    window.addEventListener('popstate', onHistoryChange);
    return () => {
      document.removeEventListener('click', beforeNavigation, true);
      window.removeEventListener('popstate', onHistoryChange);
    };
  }, [isLiveGallery, cancelTimer, clear]);
  useLayoutEffect(() => {
    const leaving = previousLiveGallery.current && !isLiveGallery;
    previousLiveGallery.current = isLiveGallery;
    if (leaving) { cancelTimer(); clear(); }
  }, [isLiveGallery, cancelTimer, clear]);
  const handleResetClick = () => {
    timer.cancel();
    clear();
  };
  const controls = <PlaybackControls
    masterVolume={masterVolume}
    onMasterVolumeChange={setMasterVolume}
    hasMix={playingSounds.length > 0}
    isPaused={isPaused}
    onTogglePause={isPaused ? resume : pause}
    timer={timer}
    showTimer={!(isGallery && calmScene)}
  />;

  return (
    <>
      <SEO url={pathname} />
      {!isLiveGallery && <h1 className="sr-only">{isGallery ? 'Nature image gallery' : pathname === '/saved' ? 'Saved sound mixes' : 'Nature sound mixer'}</h1>}
      {headerSlot && !(isGallery && calmScene) && createPortal(controls, headerSlot)}
      <div className={`mixer-page ${isLiveGallery ? "live-mixer-page" : ""}`}>
        <SoundList
          sounds={sounds}
          playMix={playMix}
          handleResetClick={handleResetClick}
          gallery={isGallery && <Suspense fallback={<div className="calm-gallery-loading" role="status">Loading scenes…</div>}><CalmGallery mixer={mixer} controls={controls} calmScene={calmScene} onCalmSceneChange={setCalmScene} onLeaveCalm={handleResetClick} /></Suspense>}
          liveGallery={isLiveGallery && <Suspense fallback={<div className="calm-gallery-loading" role="status">Loading live scene…</div>}><LiveGallery mixer={mixer} onClear={handleResetClick} /></Suspense>}
        />
        {!isGallery && !isLiveGallery && <div className="sound-grid">
          <Sound
            sounds={sounds}
            volumes={volumes}
            playingSounds={playingSounds}
            isPaused={isPaused}
            setVolume={setVolume}
            togglePlay={togglePlay}
          />
        </div>}
      </div>
    </>
  );
};

export default NatureTouch;
